// ─────────────────────────────────────────────────────────────────────
// Phone validation — mirrored across AMG modules.
// Canonical source + test suite: admin/frontend/src/utils/phone.{js,test.js}
// Keep this file in sync; run admin/frontend tests after any rule change.
// ─────────────────────────────────────────────────────────────────────
import * as Yup from 'yup'
import { isValidPhoneNumber } from 'libphonenumber-js/mobile'

/**
 * Validates a national phone number against its country's mobile number rules
 * using libphonenumber-js mobile metadata.
 *
 * Semantics are strict and framework-agnostic:
 *   - Both `phone` and `phoneCode` empty → returns true ("no value provided"; the
 *     caller's schema, e.g. Yup `.required()`, owns required-ness).
 *   - Either provided without the other → returns false (can't validate).
 *   - Phone whose first character is "0" → returns false (the national-trunk
 *     prefix is not expected when a separate dial code is supplied).
 *   - Both provided → delegates to libphonenumber-js.
 *
 * Form-UX rules (e.g. "empty value should not show an error on form open")
 * belong in the Yup schema wrapper {@link createPhoneSchema}, not here.
 *
 * @param {string|null|undefined} phone     - National phone digits without the dial
 *   code (e.g. `"7400123456"`, NOT `"+447400123456"`).
 * @param {string|null|undefined} phoneCode - International dial code with leading
 *   `+` (e.g. `"+44"`, `"+1876"`).
 * @returns {boolean} `true` only when both inputs are empty, or when the
 *   combined value is a valid mobile per libphonenumber-js. `false` for any
 *   partial presence or invalid number.
 *
 * @example
 * isPhoneValid('7400123456', '+44')  // → true
 * isPhoneValid('1234567890', '+91')  // → false (wrong prefix for India)
 * isPhoneValid(null, null)           // → true (both empty)
 * isPhoneValid('', '+44')            // → false (partial — phone missing)
 * isPhoneValid('7400123456', null)   // → false (partial — phoneCode missing)
 * isPhoneValid('0712345678', '+44')  // → false (leading zero)
 */
export const isPhoneValid = (phone, phoneCode) => {
  if (!phone && !phoneCode) return true
  if (phone && phone.charAt(0) === '0') return false
  return isValidPhoneNumber(`${phoneCode || ''}${phone || ''}`)
}

/**
 * Builds a Yup string schema that validates a phone field against country rules
 * by reading the sibling `phoneCode` field from the parent Yup.object().
 *
 * The wrapper treats an empty field value as "no value provided" and short-
 * circuits the country check, so the schema renders cleanly on form open
 * (when `react-phone-input-2` and similar widgets pre-populate `phoneCode` but
 * leave `phone` blank). Required-ness is the caller's responsibility — chain
 * `.required(msg)` on top when the field is mandatory.
 *
 * Because the test pulls `phoneCode` from `this.parent`, the schema must live
 * inside a Yup.object() where `phoneCode` is a sibling property.
 *
 * @param {string} [errorMessage='Invalid Mobile Number'] - Message shown when
 *   validation fails. Pass a translation lookup (e.g. `languageData?.invalidPhone`)
 *   for i18n; the default English string is used as a fallback.
 * @returns {import('yup').StringSchema} A Yup string schema that runs
 *   {@link isPhoneValid} against `(value, parent.phoneCode)` only when `value`
 *   is non-empty.
 *
 * @example
 * const signupSchema = Yup.object({
 *   phoneCode: Yup.string().nullable(),
 *   phone: createPhoneSchema(languageData?.invalidPhone)
 *     .required(languageData?.phoneRequired)
 *     .nullable()
 * })
 */
export const createPhoneSchema = (errorMessage = 'Invalid Mobile Number') =>
  Yup.string()
    .nullable()
    .test('phone-valid', errorMessage, function (value) {
      if (!value) return true
      return isPhoneValid(value, this.parent?.phoneCode)
    })
